import { flip } from "./lib/animation";
import datasets from "./lib/datasets";
import location from "./lib/location";
import PlaylistState from "./lib/playlist-store";
import { clone, debounce, has } from "./lib/utils";

function catalog() {
  // It's important, when assigning the DEFAULT.filters object to the filters
  // property here that you clone() it or otherwise ensure that you're not
  // giving a reference to the default filters. Othewise, subsequent
  // assignments to properties on the filters property will also be assignments
  // to this default value.
  var DEFAULT = {
    filters: {},
    viewMode: "grid",
    sortOrder: "name-asc",
    currentPage: 0,
  };

  // Regex for parsing paths that may or may not have page numbers in them. The
  // page number will be grouped separately from the rest of the path, so
  // match[1] will contain the path without the page number, and match[2] will
  // contain the page number after running
  // pathRegex.exec(window.location.pathname).
  var pathRegex = /(.*?)(?:\/(\d+))?\/?$/m;

  return {
    init() {
      var self = this;

      this.store = PlaylistState.fromLocalStorage();

      window.addEventListener("error", function (err) {
        // Only show an error if it originates from one of our scripts
        // (sometimes browser plugins error) and if we haven't already shown an
        // error.
        if (!err.filename.startsWith(window.location.origin) || self.error) {
          return;
        }
        self.error = "Something went wrong and it caught us by surprise.";
        self.catalog = null;
        window.alert(
          "Sorry! Something went wrong and it caught us by surprise. Please contact support (sos.support@noaa.gov) if you keep running into this problem."
        );
        return false;
      });

      datasets.fetchCatalog(function (err, data) {
        if (err) {
          self.error = err;
          return;
        }

        self.catalog = data;
      });

      var path = window.location.pathname,
        pageNumMatch = pathRegex.exec(path),
        data = location.decodeSearchString();

      // If there's a page number in the path (because we create static pages
      // with eleventy) extract it into our state and remove it from the path.
      // Our client-side component uses a search string parameter for the page.
      if (pageNumMatch[2]) {
        this.currentPage = Number.parseInt(pageNumMatch[2]);
      }

      /*
       * Extract non-filter values into their properties from the search string
       */

      // Page numbers in the search string override page numbers in the URL
      if (has(data, "page")) {
        this.currentPage = Number.parseInt(data.page);
        delete data.page;
      }

      if (has(data, "viewMode")) {
        this.viewMode = data.viewMode;
        delete data.viewMode;
      }

      if (has(data, "sortOrder")) {
        this.sortOrder = data.sortOrder;
        delete data.sortOrder;
      }

      this.filters = data;

      // After Alpine finishes its updates, we'll manipulate the history
      return function () {
        if (!window.history) return;

        window.history.replaceState(
          clone(self.state),
          document.title,
          self.urlFor() + window.location.hash
        );

        var firstPage = function () {
          if (self.isBrowserNavigating) return;
          self.currentPage = 0;
          self.nav();
        };

        self.$watch("filters.category", firstPage);
        self.$watch("filters.subcategory", firstPage);
        self.$watch("filters.platform", firstPage);
        self.$watch("filters.realtime", firstPage);
        self.$watch("filters.audioDescription", firstPage);
        self.$watch("filters.narratedMovie", firstPage);
        self.$watch("filters.keyword", firstPage);
        self.$watch("filters.theme", firstPage);
        self.$watch("filters.year", debounce(firstPage, 500));
        self.$watch("filters.minGrade", debounce(firstPage, 500));
        self.$watch("filters.maxGrade", debounce(firstPage, 500));
        self.$watch("filters.fullText", debounce(firstPage, 500));
        self.$watch("filters.ccc", firstPage);
        self.$watch("filters.dci", firstPage);
        self.$watch("filters.sep", firstPage);

        self.$watch("sortOrder", firstPage);

        self.$watch("viewMode", function () {
          if (self.isBrowserNavigating) return;
          self.nav();
        });

        // Scroll to the top of the page whenever we navigate to a new page.
        this.$watch("currentPage", function () {
          if (self.isBrowserNavigating) return;

          self.$nextTick(function () {
            var main = document.getElementById("main-content");
            window.scrollTo(
              0,
              window.pageYOffset + main.getBoundingClientRect().top
            );
          });
          self.nav();
        });

        window.addEventListener("popstate", function (event) {
          var state = event.state;

          self.isBrowserNavigating = true;

          // We get a null state because changing the hash (such as when using
          // "Skip to Content") triggers a popstate event. In these cases, we
          // should update our history with the current state so that
          // back/forward buttons work.
          if (state === null) {
            window.history.replaceState(
              clone(self.state),
              document.title,
              self.urlFor() + window.location.hash
            );
            self.isBrowserNavigating = false;
            return;
          }

          // Always make sure to clone the filters object before
          // reading/writing it from the history API. Otherwise you may end up
          // modifying the state stored in the history when the filters are
          // updated by the user, then we stop updating the URL because the
          // current state and our new filters are identical, so we don't
          // modify the history.
          self.filters = clone(state.filters || DEFAULT.filters);
          self.sortOrder = state.sortOrder || DEFAULT.sortOrder;
          self.viewMode = state.viewMode || DEFAULT.viewMode;
          self.currentPage = state.currentPage || DEFAULT.currentPage;
          self.isBrowserNavigating = false;
        });
      };
    },

    error: null,
    catalog: null,
    filters: clone(DEFAULT.filters),
    pageSize: 12,
    currentPage: DEFAULT.currentPage,
    sortOrder: DEFAULT.sortOrder,
    viewMode: DEFAULT.viewMode,

    /**
     * Guard to prevent watch functions firing while we handle browser
     * navigation events.
     */
    isBrowserNavigating: false,

    dateFormatter: new Intl.DateTimeFormat("en", { dateStyle: "long" }),

    get datasets() {
      return this.ready
        ? this.catalog
            .filter(datasets.filterBy(this.filters))
            .sort(datasets.compareBy(this.sortOrder))
        : [];
    },

    get page() {
      // var page = this.currentPage < this.pageCount ? this.currentPage : 0;
      // var offset = page * this.pageSize;
      var offset = this.currentPage * this.pageSize;

      // Record the positions of all existing cards so that we can animate
      // cards to their new positions.
      var positions = {};
      var cards = document.querySelectorAll(".sos-dataset:not([x-remove])");
      var n = cards.length,
        i;

      for (i = 0; i < n; i++) {
        positions[cards[i].getAttribute("id")] =
          cards[i].getBoundingClientRect();
      }

      this.$nextTick(
        flip(positions, function () {
          return document.querySelectorAll(".sos-dataset:not([x-remove])");
        })
      );

      return this.datasets.slice(offset, offset + this.pageSize);
    },

    get pageCount() {
      return Math.ceil(this.datasets.length / this.pageSize);
    },

    get ready() {
      return this.catalog !== null && this.error === null;
    },

    get state() {
      return {
        filters: this.filters,
        viewMode: this.viewMode,
        sortOrder: this.sortOrder,
        currentPage: this.currentPage,
      };
    },

    addToPlaylist(dataset, btnEl) {
      this.store.addDataset(dataset);

      this.$refs.status.innerText = btnEl.dataset.statusMsg;

      btnEl.dataset.state = "clicked";
      window.setTimeout(function () {
        btnEl.dataset.state = "ready";
      }, 1300);
    },

    nav() {
      if (!window.history) return;

      var currentState = window.history.state || DEFAULT;
      if (JSON.stringify(currentState) === JSON.stringify(this.state)) return;

      window.history.pushState(
        clone(this.state),
        document.title,
        this.urlFor()
      );
    },

    reset() {
      this.viewMode = DEFAULT.viewMode;
      this.filters = clone(DEFAULT.filters);

      if (this.sortOrder !== DEFAULT.sortOrder) {
        // If sortOrder needs to be reset, it will also reset currentPage and
        // trigger a nav(), so all we have to do is reset the sortOrder.
        this.sortOrder = DEFAULT.sortOrder;
      } else if (this.currentPage > 0) {
        // If currentPage is not the first page, reset it to 0, which will
        // trigger a nav().
        this.currentPage = 0;
      } else {
        // If sortOrder is the default order, and we are on the first page, then
        // we need to manually trigger nav().
        this.nav();
      }
    },

    showSubcategory(subcategory, parentCategories) {
      var show =
        !this.filters.category ||
        parentCategories.indexOf(this.filters.category) !== -1;

      // If we've changed to a category that doesn't contain the currently
      // selected subcategory, reset the subcategory.
      if (this.filters.subcategory === subcategory && !show) {
        this.filters.subcategory = "";
      }

      return show;
    },

    urlFor(page) {
      var pathMatch = pathRegex.exec(window.location.pathname),
        path = pathMatch[1] || window.location.pathname;

      var data = clone(this.filters);

      page = arguments.length !== 0 ? page : this.currentPage;
      if (page > 0) data.page = page;
      if (this.sortOrder !== "name-asc") data.sortOrder = this.sortOrder;
      if (this.viewMode !== "grid") data.viewMode = this.viewMode;

      var searchString = location.encodeSearchString(data);

      return path + "/" + searchString;
    },
  };
}

window.catalog = catalog;
